import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Tag,
  Button,
  Space,
  Typography,
  message,
  Dropdown,
} from 'antd';
import {
  EyeOutlined,
  MoreOutlined,
  FileTextOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import {
  getUserRequests,
  updateUserRequestStatus,
} from '../../services/userRequestService';
import UserRequestDetailDrawer from './UserRequestDetailDrawer';
import { formatDate } from '../../utils/formatters';

const { Title, Text } = Typography;

const UserRequestsList = () => {
  const [userRequests, setUserRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [detailVisible, setDetailVisible] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [nextCursor, setNextCursor] = useState(null);
  const [filters, setFilters] = useState({});

  // Fetch user requests
  const fetchUserRequests = useCallback(async (reset = false) => {
    if (loading) return;

    setLoading(true);
    try {
      const params = {
        ...filters,
        next_cursor: reset ? undefined : nextCursor,
      };

      const response = await getUserRequests(params);
      
      console.log('User requests response:', response);
      
      setUserRequests(prev => 
        reset ? response.user_requests : [...prev, ...response.user_requests]
      );
      setNextCursor(response.next_cursor);
      setHasMore(!!response.next_cursor);
    } catch (error) {
      console.error('Error fetching user requests:', error);
      message.error('Failed to fetch user requests');
    } finally {
      setLoading(false);
    }
  }, [loading, filters, nextCursor]);

  // Initial load
  useEffect(() => {
    fetchUserRequests(true);
  }, [filters]);



  // View request details
  const viewRequestDetails = useCallback(async (id) => {
    try {
      const request = userRequests.find(req => req.id === id);
      if (request) {
        setSelectedRequest(request);
        setDetailVisible(true);
      }
    } catch (error) {
      console.error('Error fetching request details:', error);
      message.error('Failed to load request details');
    }
  }, [userRequests]);

  // Handle request approval/decline
  const handleRequestAction = useCallback(async (id, action, adminComment = '') => {
    try {
      setStatusLoading(true);
      await updateUserRequestStatus(id, action, adminComment);

      // Update the request in the list
      setUserRequests(prev =>
        prev.map(req =>
          req.id === id
            ? { ...req, status: action === 'approve' ? 'approved' : 'declined' }
            : req
        )
      );

      // Update selected request if it's currently being viewed
      if (selectedRequest && selectedRequest.id === id) {
        setSelectedRequest(prev => ({
          ...prev,
          status: action === 'approve' ? 'approved' : 'declined'
        }));
      }

      message.success(`Request ${action === 'approve' ? 'approved' : 'declined'} successfully`);
    } catch (error) {
      console.error(`Error ${action}ing request:`, error);
      message.error(`Failed to ${action} request`);
    } finally {
      setStatusLoading(false);
    }
  }, [selectedRequest]);

  // Get status color
  const getStatusColor = (status) => {
    const statusMap = {
      pending: 'warning',
      approved: 'success',
      declined: 'error',
    };
    return statusMap[status] || 'default';
  };

  // Get status display text
  const getStatusDisplayText = (status) => {
    const statusTextMap = {
      pending: 'Pending Review',
      approved: 'Approved',
      declined: 'Declined',
    };
    return statusTextMap[status] || status;
  };



  // Table columns
  const columns = [
    {
      title: 'Request ID',
      dataIndex: 'id',
      key: 'id',
      width: 250,
      render: (id) => (
        <Space>
          <Text code style={{ fontSize: '12px' }}>
            {id}
          </Text>
          <Button
            type="text"
            size="small"
            icon={<CopyOutlined />}
            onClick={() => {
              navigator.clipboard.writeText(id);
              message.success('Request ID copied to clipboard');
            }}
            style={{ padding: '0 4px' }}
          />
        </Space>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 150,
      render: (type) => (
        <Tag color="blue">
          {type === 'account-upgrade' ? 'Account Upgrade' : type}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {getStatusDisplayText(status)}
        </Tag>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'created',
      key: 'created',
      width: 150,
      render: (timestamp) => formatDate(timestamp),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      render: (_, record) => {
        const menuItems = [
          {
            key: 'view',
            icon: <EyeOutlined />,
            label: 'View Details',
            onClick: () => viewRequestDetails(record.id),
          },
        ];

        return (
          <Dropdown
            menu={{ items: menuItems }}
            trigger={['click']}
          >
            <Button
              type="text"
              icon={<MoreOutlined />}
              style={{ border: 'none' }}
            />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <>
      <div style={{ marginBottom: 24 }}>
        <Title level={4} style={{ margin: 0 }}>
          Upgrade Requests
        </Title>
      </div>
        <Table
          dataSource={userRequests}
          columns={columns}
          rowKey="id"
          pagination={false}
          loading={loading}
          scroll={{ x: 800 }}
          locale={{
            emptyText: (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <FileTextOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
                <Text type="secondary">No upgrade requests found</Text>
              </div>
            ),
          }}
        />

        {hasMore && (
          <div style={{ textAlign: 'center', marginTop: '16px' }}>
            <Button
              onClick={() => fetchUserRequests(false)}
              loading={loading}
              type="dashed"
            >
              Load More
            </Button>
          </div>
        )}

      

      {/* Request Detail Drawer */}
      <UserRequestDetailDrawer
        visible={detailVisible}
        request={selectedRequest}
        onClose={() => {
          setDetailVisible(false);
          setSelectedRequest(null);
        }}
        onAction={handleRequestAction}
        loading={statusLoading}
      />
    </>
  );
};

export default UserRequestsList;
